import { useCallback, useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogFooter,
  DialogTitle,
} from '@/components/ui/dialog';
import { CalendarClock, Loader2, MapPin, Pencil, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import {
  buildWwEditJsonUrl,
  buildWwEventApiBase,
  formatEventTime,
  fromDateTimeLocal,
  toDateTimeLocal,
  cleanEventTitleAndLocation,
  labelFromLocationHtml,
} from './wwUi';
import { calendarHtml } from '@/utils/safeHtml';

// A CalOne calendar event (the shape stored in static/json/<season>/omega/<CODE>.json).
interface CalOneEvent {
  id: number;
  title?: string;
  start?: string;
  end?: string;
  location?: string;
  description?: string;
  source?: 'master' | 'group' | 'override' | string;
  calendarId?: number | string;
  raw?: {
    caloneBaseTitle?: string;
    caloneLocationText?: string;
    caloneLocationSource?: string;
    caloneLocationHtml?: string;
    caloneTitleLocationHtml?: string;
    caloneTitleLocationManaged?: boolean;
  };
}

// Teknostart (NTNU's central programme) lives on calendar id 3 and is merged in
// read-only; it is managed by NTNU, not editable from this app.
const TEKNOSTART_CALENDAR_ID = '3';

interface FormState {
  title: string;
  start: string; // datetime-local
  end: string; // datetime-local
  location: string;
  description: string;
}

const EMPTY_FORM: FormState = { title: '', start: '', end: '', location: '', description: '' };

interface Props {
  /** Calendar season code, e.g. "26h". */
  season: string;
  /** Group filename stem, e.g. "PI" (or "master" for the master programme). */
  code: string;
  /** Group public slug, e.g. "pi" (or "master"). */
  slug: string;
  /** The admin's edit pin for this group / the master. */
  pin: string;
  /**
   * Which calendar this editor drives. In "group" mode only the group's own
   * additions are editable and master-sourced rows are read-only. In "master"
   * mode every Omega programme row is editable; only Teknostart (managed by
   * NTNU) stays read-only.
   */
  kind?: 'group' | 'master';
}

/**
 * Mobile-friendly list editor for an Omega calendar. Reads the same pin-gated
 * CalOne JSON the fullview loads and mutates it through the per-event API
 * (add/edit/delete), so it stays perfectly in sync with the fullview + ICS.
 *
 * In group mode, master-sourced events are read-only (managed centrally) and
 * only the group's own additions can be edited or removed. In master mode the
 * whole Omega programme is editable; only Teknostart rows are read-only.
 */
export default function GroupEventEditor({ season, code, slug, pin, kind = 'group' }: Props) {
  const { slug: orgSlug } = useParams<{ slug: string }>();
  const isMaster = kind === 'master';
  const [events, setEvents] = useState<CalOneEvent[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [open, setOpen] = useState(false);
  const [editingId, setEditingId] = useState<number | 'new' | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY_FORM);

  const jsonUrl = buildWwEditJsonUrl(season, code, pin, orgSlug);
  const apiBase = buildWwEventApiBase(slug, pin, orgSlug);

  const load = useCallback(async () => {
    if (!jsonUrl) return;
    setLoading(true);
    try {
      const res = await fetch(`${jsonUrl}?${Date.now()}`, { cache: 'no-store' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const doc = await res.json();
      const list: CalOneEvent[] = Array.isArray(doc?.events) ? doc.events : [];
      list.sort((a, b) => String(a.start || '').localeCompare(String(b.start || '')));
      setEvents(list);
    } catch (error) {
      console.error('[GroupEventEditor] load failed', error);
      toast.error('Could not load calendar events.');
    } finally {
      setLoading(false);
    }
  }, [jsonUrl]);

  useEffect(() => {
    void load();
  }, [load]);

  useEffect(() => {
    const handler = (event: MessageEvent) => {
      if ((event.data?.type === 'omega-action-success' || event.data?.type === 'ww-action-success')) {
        console.log('[GroupEventEditor] received success message from iframe, refreshing list');
        void load();
      }
    };
    window.addEventListener('message', handler);
    return () => window.removeEventListener('message', handler);
  }, [load]);

  const openAdd = () => {
    setEditingId('new');
    setForm(EMPTY_FORM);
    setOpen(true);
  };

  const openEdit = (event: CalOneEvent) => {
    setEditingId(event.id);
    const { title, location } = cleanEventTitleAndLocation(event);
    setForm({
      title,
      start: toDateTimeLocal(event.start),
      end: toDateTimeLocal(event.end),
      location,
      description: event.description || '',
    });
    setOpen(true);
  };

  const submit = async () => {
    if (!form.title.trim()) {
      toast.error('Title is required.');
      return;
    }
    if (!form.start || !form.end) {
      toast.error('Start and end times are required.');
      return;
    }
    const payload = {
      title: form.title.trim(),
      start: fromDateTimeLocal(form.start),
      end: fromDateTimeLocal(form.end),
      location: form.location.trim(),
      description: form.description.trim(),
    };
    setSaving(true);
    try {
      const isNew = editingId === 'new';
      const url = isNew ? apiBase : `${apiBase}/${editingId}`;
      const res = await fetch(url, {
        method: isNew ? 'POST' : 'PATCH',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      toast.success(isNew ? 'Event added.' : 'Event updated.');
      setOpen(false);
      await load();
    } catch (error) {
      console.error('[GroupEventEditor] save failed', error);
      toast.error(`Could not save event: ${(error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  const remove = async (event: CalOneEvent) => {
    if (!window.confirm(`Delete "${labelFromLocationHtml(event.title || '') || 'this event'}"? This cannot be undone.`)) return;
    setSaving(true);
    try {
      const res = await fetch(`${apiBase}/${event.id}`, { method: 'DELETE' });
      const body = await res.json().catch(() => ({}));
      if (!res.ok || body?.ok === false) {
        throw new Error(body?.error || `HTTP ${res.status}`);
      }
      toast.success('Event deleted.');
      await load();
    } catch (error) {
      console.error('[GroupEventEditor] delete failed', error);
      toast.error(`Could not delete event: ${(error as Error).message}`);
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardContent className="p-4 space-y-4">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
          <div className="flex flex-wrap items-center gap-2 text-sm font-medium text-muted-foreground min-w-0">
            <Pencil className="h-4 w-4 shrink-0" />
            <span className="shrink-0">Edit events</span>
            <Badge variant="secondary" className="max-w-full break-words">Mobile</Badge>
          </div>
          <Button size="sm" onClick={openAdd} disabled={saving} className="w-full sm:w-auto">
            <Plus className="h-4 w-4 mr-1.5" />
            Add event
          </Button>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-8 text-muted-foreground">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        ) : events.length === 0 ? (
          <p className="py-6 text-center text-sm text-muted-foreground">No events yet.</p>
        ) : (
          <div className="grid gap-2">
            {events.map((event) => {
              const isCentralSource = String(event.calendarId) === TEKNOSTART_CALENDAR_ID;
              const centralLabel = 'Central programme';
              const isGroup = event.source === 'group';
              const isOverride = event.source === 'override';
              // In a group, editing a master row turns it into a local "override"
              // (a blue group change) and deleting it tombstones the master row;
              // Teknostart stays read-only there (NTNU-managed). The master file
              // (mtelsys_*) is Omega's own copy, so the master admin may edit
              // everything, Teknostart included.
              const editable = isMaster || !isCentralSource;
              const badgeLabel = isMaster
                ? (isCentralSource ? centralLabel : (orgSlug?.toLowerCase() === 'omega2' ? 'Omega2' : 'Omega'))
                : (isGroup ? 'Group' : isOverride ? 'Changed' : 'Master');
              return (
                <div key={event.id} className="space-y-1.5 rounded-md border p-3">
                  <div className="flex items-center justify-between gap-2">
                    <Badge
                      variant={editable ? 'default' : 'outline'}
                      className="shrink-0"
                      style={badgeLabel === 'Master' ? { backgroundColor: '#366EB2', color: '#ffffff', borderColor: '#366EB2' } : undefined}
                    >
                      {badgeLabel}
                    </Badge>
                    {editable && (
                      <div className="-mr-1 flex shrink-0 gap-0.5">
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8"
                          onClick={() => openEdit(event)}
                          disabled={saving}
                          aria-label="Edit event"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          className="h-8 w-8 text-destructive"
                          onClick={() => remove(event)}
                          disabled={saving}
                          aria-label="Delete event"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                  <h3
                    className="break-words font-medium leading-snug [&_a]:underline [&_a]:text-primary"
                    dangerouslySetInnerHTML={calendarHtml(event.title || 'Untitled')}
                  />
                  <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                    <CalendarClock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span className="break-words">
                      {event.start && event.end ? formatEventTime(event.start, event.end) : '—'}
                    </span>
                  </p>
                  {event.location && (
                    <p className="flex items-start gap-1.5 text-xs text-muted-foreground">
                      <MapPin className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                      <span
                        className="min-w-0 break-words [&_a]:underline [&_a]:text-primary"
                        dangerouslySetInnerHTML={calendarHtml(event.location)}
                      />
                    </p>
                  )}
                </div>
              );
            })}
          </div>
        )}

        <p className="text-xs text-muted-foreground">
          {isMaster
            ? (orgSlug?.toLowerCase() === 'omega2'
                ? 'These are the master Omega2 programme events — Omega 2-årig Master Fadderprogram — all editable here. Changes update the master calendar and flow out to every group on its next refresh.'
                : 'These are the master Omega programme events — Omega Fadderprogram plus this Omega copy of Teknostart — all editable here. Changes update the master calendar and flow out to every group on its next refresh.')
            : 'Editing a master event makes a local change for this group (shown as "Changed"); deleting one removes it for this group only. Both survive refreshes from the master. Central programme events are managed centrally and are read-only.'}
        </p>
      </CardContent>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="sm:max-w-md">
          <form
            onSubmit={(e) => {
              e.preventDefault();
              void submit();
            }}
            className="space-y-4"
          >
            <DialogHeader>
              <DialogTitle>{editingId === 'new' ? 'Add event' : 'Edit event'}</DialogTitle>
            </DialogHeader>
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label htmlFor="ev-title">Title</Label>
                <Input
                  id="ev-title"
                  value={form.title}
                  onChange={(e) => setForm((f) => ({ ...f, title: e.target.value }))}
                  placeholder="e.g. Pre-party"
                />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1.5">
                  <Label htmlFor="ev-start">Start</Label>
                  <Input
                    id="ev-start"
                    type="datetime-local"
                    value={form.start}
                    onChange={(e) => setForm((f) => ({ ...f, start: e.target.value }))}
                  />
                </div>
                <div className="space-y-1.5">
                  <Label htmlFor="ev-end">End</Label>
                  <Input
                    id="ev-end"
                    type="datetime-local"
                    value={form.end}
                    onChange={(e) => setForm((f) => ({ ...f, end: e.target.value }))}
                  />
                </div>
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ev-location">Location</Label>
                <Input
                  id="ev-location"
                  value={form.location}
                  onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
                  placeholder="e.g. Moholt"
                />
              </div>
              <div className="space-y-1.5">
                <Label htmlFor="ev-description">Description</Label>
                <Textarea
                  id="ev-description"
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  rows={3}
                />
              </div>
            </div>
            <DialogFooter>
              <Button type="button" variant="ghost" onClick={() => setOpen(false)} disabled={saving}>
                Cancel
              </Button>
              <Button type="submit" disabled={saving}>
                {saving && <Loader2 className="h-4 w-4 mr-1.5 animate-spin" />}
                {editingId === 'new' ? 'Add' : 'Save'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
